/**
 * @file LoginForm.js
 * @description Represents the initial login form with mobile responsiveness
 */
import { useState, useEffect, useRef } from 'react';
import { requestToken } from '../../services/authService';
import { logAnonymousEvent } from '../../services/loggingService';
import Toast from '../ui/toasts/Toast';
import { useTheme } from '../../store/ThemeContext';
import Spinner from '../ui/spinners/Spinner';
import { useSearchParams } from 'react-router-dom';
import PasswordInput from '../ui/PasswordInput';
import style from './AuthPage.module.scss';
import PlaceholderLogo from '../ui/PlaceholderLogo';
import { getText } from '../../i18n';

/**
 * Represents a login form component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.onSuccess - The callback function to be called on successful login.
 * @param {Function} props.handleAuthError - The callback function to handle authentication errors.
 * @param {Object} props.authData - The authentication data.
 * @param {boolean} [props.demoOnly=false] - Flag indicating whether the form is for demo purposes only.
 * @param {Object} [props.styleOverride={}] - The style override object for the form.
 * @returns {JSX.Element} The rendered login form.
 */
function LoginForm({
    onSuccess,
    handleAuthError,
    authData,
    demoOnly = false, // This key enable the use of the Auth form within the Theme page.
    styleOverride = {}, // This override is to be used by the theme page
    onBeforeSubmit = null,
}) {
    const { companyCode, companyConfig } = authData;
    const themeStyles = useTheme();
    const [auth, setAuth] = useState({
        username: '',
        password: '',
        companyCode: companyCode || null,
        companyConfig: companyConfig || null,
        rememberMe: false,
    });
    const [errors, setErrors] = useState({
        username: '',
        password: '',
        companyCode: '',
    });
    const companyCodeRef = useRef(null);
    const usernameRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const [transitionEmail, setTransitionEmail] = useState('');

    useEffect(() => {
        if (companyCode) {
            if (!demoOnly) usernameRef.current?.focus();
        } else {
            if (!demoOnly) companyCodeRef.current?.focus();
        }

        if (demoOnly) {
            const companyCode =
                window.localStorage.getItem('companyCode') || 'ingaged';
            setAuth({
                username: '',
                password: '',
                companyCode: companyCode,
                companyConfig: companyConfig || null,
            });
        }
    }, [companyCode]);

    useEffect(() => {
        const usernameFromUrl = searchParams.get('username');
        if (usernameFromUrl) {
            setTransitionEmail(usernameFromUrl);
            // Optionally pre-fill username field if desired
            setAuth(prev => ({
                ...prev,
                username: usernameFromUrl,
            }));
        }
    }, [searchParams]);

    const handleSubmit = async event => {
        event.preventDefault();
        if (onBeforeSubmit) onBeforeSubmit();
        if (validateForm()) {
            if (demoOnly) return;
            try {
                setIsLoading(true);
                const response = await requestToken(
                    auth.username,
                    auth.password,
                    auth.companyCode,
                    auth.rememberMe,
                );

                onSuccess({
                    username: auth.username,
                    password: auth.password,
                    companyCode: auth.companyCode,
                    rememberMe: auth.rememberMe,
                    ...response,
                });
            } catch (error) {
                console.error('Login failed:', error);

                if (error?.response?.status === 403) {
                    if (error?.response?.data?.code === 'USER_LOCKOUT') {
                        Toast.error(
                            error?.response?.data?.detail,
                            null,
                            getText('msg_63b31fd75021'),
                        );
                    } else if (
                        error?.response?.data?.code === 'TENANT_NOT_FOUND'
                    ) {
                        Toast.error(
                            error?.response?.data?.detail,
                            null,
                            getText('msg_63b31fd75022'),
                        );
                    } else {
                        Toast.error(getText('msg_4f4b9008c432'));
                    }
                }

                if (error?.response?.status === 500) {
                    Toast.error(getText('msg_37b8ed1700e1'));
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const validateForm = () => {
        const newErrors = {
            username: '',
            password: '',
            companyCode: '',
        };
        let isFormValid = true;

        if (!auth.username.trim()) {
            newErrors.username = 'Required';
            isFormValid = false;
        }

        if (!auth.password.trim()) {
            newErrors.password = 'Required';
            isFormValid = false;
        }

        if (!auth.companyCode.trim()) {
            newErrors.companyCode = 'Required';
            isFormValid = false;
        }

        setErrors(newErrors);
        return isFormValid;
    };

    const handleChange = event => {
        const { name, value } = event.target;
        setAuth({ ...auth, [name]: value });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRememberMeChange = event => {
        setAuth(prev => {
            return {
                ...prev,
                rememberMe: event.target.checked,
            };
        });
    };

    return (
        <form
            className="form-container px-2 px-md-2"
            onSubmit={handleSubmit}
            noValidate
        >
            <div
                className={`form-group text-center mb-3 mb-md-5  ${style.logoContainer}`}
            >
                {styleOverride?.primaryLogo ||
                themeStyles?.primaryLogo ||
                (auth.companyCode === 'swmockup' && '/SWMockup.png') ? (
                    <img
                        src={
                            styleOverride?.primaryLogo ||
                            themeStyles?.primaryLogo ||
                            (auth.companyCode === 'swmockup' && '/SWMockup.png')
                        }
                        alt={getText('msg_101d2406160e')}
                        className="img-fluid"
                    />
                ) : (
                    <PlaceholderLogo />
                )}
                <span className="d-block m-auto mt-2 mt-md-3">
                    {getText('msg_983056cb109c')}{' '}
                    <a
                        onClick={e => {
                            if (demoOnly) {
                                e.preventDefault();
                                return;
                            }
                        }}
                        href={`/onboarding/anonymous/find-user${
                            companyCode ? `?companyCode=${companyCode}` : ''
                        }`}
                        className="pr-1 pr-md-2"
                    >
                        {getText('msg_eff4fd865f1a')}
                    </a>
                </span>
            </div>
            <div
                className={`form-group mb-2 mb-md-3 ${companyConfig ? 'd-none' : ''}`}
            >
                <label htmlFor="companyCode" className="mb-1">
                    {getText('msg_19213d874eed')}
                </label>
                <input
                    type="text"
                    className={`form-control ${errors.companyCode ? 'is-invalid' : ''}`}
                    placeholder={getText('msg_19213d874eed')}
                    name="companyCode"
                    id="companyCode"
                    autoComplete="on"
                    onChange={handleChange}
                    value={auth.companyCode}
                    aria-describedby="building-icon"
                    ref={companyCodeRef}
                    disabled={isLoading}
                />
                {errors.companyCode && (
                    <div className="invalid-feedback">{errors.companyCode}</div>
                )}
            </div>
            <div className="form-group mb-2 mb-md-3">
                <label htmlFor="username" className="mb-1">
                    {getText('msg_84c29015de33')}
                </label>
                <input
                    type="text"
                    className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                    placeholder={getText('msg_4b2f2bb2bca7')}
                    name="username"
                    id="username"
                    onChange={handleChange}
                    value={auth.username || transitionEmail}
                    aria-describedby="person-icon"
                    ref={usernameRef}
                    disabled={isLoading}
                />
                {errors.username && (
                    <div className="invalid-feedback">{errors.username}</div>
                )}
            </div>
            <div className="form-group mb-2 mb-md-3">
                <label htmlFor="password" className="mb-1">
                    {getText('msg_8be3c943b160')}
                </label>
                <PasswordInput
                    id="password"
                    name="password"
                    value={auth.password}
                    onChange={handleChange}
                    placeholder={getText('msg_8be3c943b160')}
                    className={errors.password ? 'is-invalid' : ''}
                    disabled={isLoading}
                    required
                    autoComplete="off"
                    errorMessage={errors.password}
                />
            </div>

            <div
                style={{
                    margin: '0px 0px 8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <span style={{ display: 'inline-block', marginRight: '16px' }}>
                    <input
                        id="rememberMe"
                        type="checkbox"
                        className="form-check-input custom-checkbox"
                        name="rememberMe"
                        onChange={handleRememberMeChange}
                        style={{ verticalAlign: 'middle' }}
                    />
                </span>
                <span
                    style={{
                        color: '#2F2B43',
                        fontWeight: '500',
                        fontSize: '16px',
                    }}
                >
                    {getText('msg_c2cfd0df1f88')}
                </span>
            </div>
            <span
                style={{
                    color: 'gray',
                    fontSize: '12px',
                    marginLeft: '48px',
                    display: 'block',
                }}
            >
                {getText('msg_6e5cbf03a45d')}
            </span>

            <div className="form-group text-center pt-2 pt-md-3 pb-1 pb-md-2">
                {isLoading ? (
                    <div className="m-auto d-flex justify-content-center">
                        <Spinner width={40} />
                    </div>
                ) : (
                    <button
                        type="submit"
                        className="ing-button-primary-left m-auto w-100 w-md-auto"
                        style={{
                            ...(styleOverride?.accessoryColor && {
                                backgroundColor: styleOverride?.accessoryColor,
                            }),
                            ...(styleOverride?.accessoryColor && {
                                border: `1px solid ${styleOverride?.accessoryColor}`,
                            }),
                            ...(styleOverride?.accessoryTextColor && {
                                color: styleOverride?.accessoryTextColor,
                            }),
                            padding: '10px 30px',
                            minWidth: '120px',
                        }}
                    >
                        {getText('msg_d527bf3db3dc')}
                    </button>
                )}
            </div>
            <div className="form-group text-center pt-4">
                <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center">
                    <a
                        onClick={e => {
                            if (demoOnly) {
                                e.preventDefault();
                                return;
                            }
                        }}
                        href={`/forgot-email${
                            companyCode ? `?companyCode=${companyCode}` : ''
                        }`}
                        className={`${style.recoveryBtns} mb-2 mb-sm-0`}
                    >
                        {getText('msg_4e7458fb72a2')}
                    </a>
                    <span className="d-none d-sm-inline mx-2">|</span>
                    <a
                        onClick={e => {
                            if (demoOnly) {
                                e.preventDefault();
                                return;
                            }
                        }}
                        href={`/forgot-password${
                            companyCode ? `?companyCode=${companyCode}` : ''
                        }`}
                        className={`${style.recoveryBtns}`}
                    >
                        {getText('msg_f99d98e85f90')}
                    </a>
                </div>
            </div>
            <div className="form-group text-center pt-4">
                <p>
                    <i className="bi bi-envelope" />{' '}
                    {getText('msg_97e2e9fd5798')}{' '}
                    <a
                        onClick={e => {
                            if (demoOnly) {
                                e.preventDefault();
                                return;
                            }
                        }}
                        href={`/get-support${
                            companyCode ? `?companyCode=${companyCode}` : ''
                        }`}
                        className="pl-2"
                    >
                        {getText('msg_fe1a2922069b')}
                    </a>
                </p>
            </div>
        </form>
    );
}

export default LoginForm;
